"use client";

import { motion, useScroll, useTransform } from "framer-motion";
import {
  Bot,
  FileText,
  LineChart,
  Network,
  Pause,
  Play,
  Shield,
  Zap,
} from "lucide-react";
import Image from "next/image";
import { useState } from "react";
import heroImage from "@/public/images/dayttagangwork.png";

const accents = [
  { Icon: LineChart, position: "left-[9%] top-0", delay: "0s" },
  { Icon: Shield, position: "right-[13%] top-1", delay: "-1.5s" },
  { Icon: Network, position: "bottom-0 left-[13%]", delay: "-3s" },
  { Icon: Zap, position: "right-0 top-[37%]", delay: "-0.8s" },
  { Icon: Bot, position: "bottom-1 right-[12%]", delay: "-2.2s" },
  { Icon: FileText, position: "left-0 top-[40%]", delay: "-4s" },
];

/** Original brand artwork, with motion independent of the readable hero copy. */
export function HeroIllustration() {
  const [pausedOffset, setPausedOffset] = useState<number | null>(null);
  const { scrollY } = useScroll();
  const parallaxY = useTransform(scrollY, [0, 700], [0, -24]);
  const paused = pausedOffset !== null;

  return (
    <figure className="hero-artwork hero-enter stagger-2" data-paused={paused}>
      <div className="hero-artwork-glow" aria-hidden />
      <motion.div
        className="hero-artwork-parallax relative py-12"
        style={{ y: pausedOffset ?? parallaxY }}
      >
        <div className="mx-3 overflow-hidden rounded-xl border border-border bg-card p-1.5 shadow-elegant-lg sm:p-2">
          <Image
            src={heroImage}
            alt="The Kautious turtle, Daytta, and Llamox llama working together at a research desk"
            className="h-auto w-full rounded-lg"
            sizes="(min-width: 1440px) 560px, (min-width: 768px) calc(50vw - 64px), calc(100vw - 64px)"
            loading="eager"
            fetchPriority="high"
            placeholder="blur"
          />
        </div>
        <div className="pointer-events-none absolute inset-0" aria-hidden>
          {accents.map(({ Icon, position, delay }) => (
            <span key={delay} className={`absolute ${position}`}>
              <span
                className="hero-floating-icon"
                style={{ animationDelay: delay }}
              >
                <Icon className="size-4 sm:size-5" />
              </span>
            </span>
          ))}
        </div>
      </motion.div>
      <figcaption className="relative mt-1 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-t pt-3">
        <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
          Kautious · Daytta · Llamox
        </span>
        {/* CSS keeps the preference response immediate and SSR markup stable. */}
        <span className="hidden py-3 text-xs text-muted-foreground motion-reduce:inline">
          Reduced motion enabled
        </span>
        <button
          type="button"
          onClick={() =>
            setPausedOffset((offset) =>
              offset === null ? parallaxY.get() : null,
            )
          }
          aria-label={paused ? "Resume hero animation" : "Pause hero animation"}
          className="inline-flex min-h-11 items-center gap-2 rounded px-2 text-xs text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring motion-reduce:hidden"
        >
          {paused ? (
            <Play className="size-3" aria-hidden />
          ) : (
            <Pause className="size-3" aria-hidden />
          )}
          {paused ? "Resume motion" : "Pause motion"}
        </button>
      </figcaption>
    </figure>
  );
}
